import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A fortune-teller's crystal ball: a plump pale-cyan orb cradled on a velvet
// cushion atop an ornate little golden stand. Built to glow — a bright core
// sphere hides just under the orb's skin and swells past it on the beat, so the
// whole ball flashes bright ("idle"); three tiny star sparkles share a name and
// drift in a slow tilted orbit around the orb ("orbit"). Storybook batch:
// plump masses, oversized stand feet and piping, warm saturated colours +
// cream trim.
//
// Frame: y = 0 is the ground, +Z is the front. Roughly 0.6 m tall over a
// 1x1 tile.

const GOLD = "#e3a83c";        // ornate stand gold
const GOLD_DARK = "#b57f24";   // stand shading (feet, under-ring)
const VELVET = "#8e3fae";      // royal purple cushion
const VELVET_DARK = "#6d2c88"; // cushion shading (piping seam)
const CREAM = "#f6efe3";       // cream trim piping
const BALL = "#aee6ef";        // pale-cyan crystal skin
const CORE = "#eafdff";        // bright inner glow core
const SPARK = "#d9f8ff";       // orbiting sparkle stars

// ── frame ────────────────────────────────────────────────────────────────────
const BASE_H = 0.05;                  // stand base plate height
const CUSHION_Y = BASE_H + 0.055;     // cushion centre height
const BALL_R = 0.185;                 // crystal orb radius
const BALL_CY = CUSHION_Y + 0.05 + BALL_R; // orb centre (~0.34), top ~0.53

// The glow pulses and the sparkles orbit about the orb's centre.
const BALL_PIVOT: [number, number, number] = [0, BALL_CY, 0];

// One stubby claw foot: a squashed golden ball peeking out from under the base
// plate, so the stand reads planted and toy-chunky rather than flat.
function foot(angle: number): Part {
  return {
    geometry: new THREE.SphereGeometry(0.045, 8, 6), color: GOLD_DARK,
    position: [Math.sin(angle) * 0.15, 0.03, Math.cos(angle) * 0.15],
    scale: [1, 0.75, 1],
  };
}

// One orbiting sparkle: a tiny star-like gem. All three share the name
// "sparkles" so the orbit track spins them as one ring about the orb.
function sparkle(angle: number, r: number, lift: number, size: number): Part {
  return {
    geometry: new THREE.OctahedronGeometry(size, 0), color: SPARK,
    position: [Math.sin(angle) * r, BALL_CY + lift, Math.cos(angle) * r],
    name: "sparkles",
  };
}

export const crystalBall = defineModel({
  id: "crystal-ball",
  batchId: "mess",
  name: "Crystal ball",
  tags: ["mess", "cute", "animated", "prop", "magic", "fortune", "fantasy"],
  summary: "A glowing pale-cyan crystal ball on a golden stand with a velvet cushion.",
  description:
    "A plump toy-like crystal ball: a pale-cyan orb with a bright inner core, " +
    "seated on a royal purple velvet cushion with cream piping and gold " +
    "tassels, atop an ornate little golden stand with claw feet. The idle " +
    "animation pulses the inner glow so the whole orb flashes bright; the " +
    "orbit animation drifts three tiny sparkle stars in a slow ring around it.",
  author: "models",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // Ornate golden stand: three claw feet under a fat base plate with a
      // rolled ring — oversized, so the little stand reads deliberate.
      foot(0), foot((Math.PI * 2) / 3), foot((Math.PI * 4) / 3),
      { geometry: new THREE.CylinderGeometry(0.16, 0.19, BASE_H, 12), color: GOLD,
        position: [0, 0.025 + BASE_H / 2, 0] },
      { geometry: new THREE.TorusGeometry(0.155, 0.022, 8, 16), color: GOLD_DARK,
        position: [0, BASE_H + 0.02, 0], rotation: [Math.PI / 2, 0, 0] },

      // Velvet cushion: a plump squashed sphere with a cream piping seam and
      // four little gold tassels at the corners.
      { geometry: new THREE.SphereGeometry(0.17, 12, 8), color: VELVET,
        position: [0, CUSHION_Y, 0], scale: [1.12, 0.55, 1.12] },
      { geometry: new THREE.TorusGeometry(0.19, 0.024, 8, 20), color: CREAM,
        position: [0, CUSHION_Y, 0], rotation: [Math.PI / 2, 0, 0] },
      ...[Math.PI / 4, (Math.PI * 3) / 4, (Math.PI * 5) / 4, (Math.PI * 7) / 4].map(
        (a): Part => ({
          geometry: new THREE.SphereGeometry(0.028, 6, 5), color: GOLD,
          position: [Math.sin(a) * 0.165, CUSHION_Y - 0.015, Math.cos(a) * 0.165],
        }),
      ),
      // A dark seat dimple where the orb presses into the velvet.
      { geometry: new THREE.CylinderGeometry(0.11, 0.13, 0.03, 12), color: VELVET_DARK,
        position: [0, CUSHION_Y + 0.045, 0] },

      // The crystal orb, with the bright glow core hiding just under its skin —
      // the idle pulse swells the core past the skin so the whole ball flashes.
      { geometry: new THREE.SphereGeometry(BALL_R, 16, 12), color: BALL,
        position: [0, BALL_CY, 0] },
      { geometry: new THREE.SphereGeometry(BALL_R * 0.94, 14, 10), color: CORE,
        position: [0, BALL_CY, 0], name: "core" },

      // Three tiny sparkle stars caught mid-orbit at staggered heights.
      sparkle(0, BALL_R + 0.075, 0.02, 0.032),
      sparkle((Math.PI * 2) / 3, BALL_R + 0.06, 0.1, 0.025),
      sparkle((Math.PI * 4) / 3, BALL_R + 0.07, -0.07, 0.028),
    ];
  },
  animations: [
    // 1. Idle — the inner glow breathes: the core swells just past the skin so
    //    the whole orb flashes bright, then sinks back to pale cyan.
    {
      id: "idle",
      label: "Glow pulse",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "core", pivot: BALL_PIVOT,
          scale: [
            { t: 0, value: 0.96, ease: "sine" },
            { t: 1.4, value: 1.1, ease: "sine" },
            { t: 2.8, value: 0.96, ease: "sine" },
          ],
        },
        // The sparkles twinkle faintly in counter-phase to the flash.
        {
          target: "sparkles", pivot: BALL_PIVOT,
          scale: [
            { t: 0, value: 1.15, ease: "sine" },
            { t: 1.4, value: 0.8, ease: "sine" },
            { t: 2.8, value: 1.15, ease: "sine" },
          ],
        },
      ],
    },
    // 2. Orbit — the signature move: the sparkle ring drifts once around the
    //    orb, bobbing gently as it goes, while the core keeps a soft shimmer.
    {
      id: "orbit",
      label: "Sparkle orbit",
      duration: 6,
      loop: true,
      tracks: [
        {
          target: "sparkles", pivot: BALL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 3, value: [0, Math.PI, 0] },
            { t: 6, value: [0, Math.PI * 2, 0] },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0, 0.035, 0], ease: "sine" },
            { t: 4.5, value: [0, -0.035, 0], ease: "sine" },
            { t: 6, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "core", pivot: BALL_PIVOT,
          scale: [
            { t: 0, value: 1.0, ease: "sine" },
            { t: 3, value: 1.06, ease: "sine" },
            { t: 6, value: 1.0, ease: "sine" },
          ],
        },
      ],
    },
  ],
});
